/* eslint-disable react-refresh/only-export-components -- DEFAULT_PAGE_SIZE_OPTIONS is this component's own fallback, shared with callers that need the same default */
import { useId } from 'react';

import { DimeField, DimeFieldLabel } from '../DimeField.js';
import { DimeSelect, DimeSelectOption } from '../DimeSelect.js';
import { DataTablePaginationNav } from './DataTablePaginationNav.js';
import { formatPaginationPageText } from './format-pagination-page-text.js';
import { formatPaginationRangeText } from './format-pagination-range-text.js';

/** The paginatable-table surface this footer needs — narrower than a full DimeDataTable table, so
 *  tables built on a feature set without filtering or row selection (DimeNestedTable) can use it.
 *  The row-model getters live only on a selection-capable feature set, so they are optional here
 *  and read only under showSelect, which such tables never pass. Same structural-narrowing
 *  technique as DataTableColumnHeader's SortableColumn. */
interface PaginatableTable {
  getFilteredSelectedRowModel?: () => { rows: readonly unknown[] };
  getFilteredRowModel?: () => { rows: readonly unknown[] };
}

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface DataTablePaginationProps {
  table: PaginatableTable;
  showSelect?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onFirstPage?: () => void;
  onLastPage?: () => void;
  showFirstLast?: boolean;
  pageIndex?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  // Rows rendered on this page, not the dataset total.
  rowCount?: number;
  totalCount?: number;
  // hasNextPage/hasPreviousPage and their cursors describe the last settled page until the pending fetch lands.
  isLoading?: boolean;
}

export function DataTablePagination({
  table,
  showSelect,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  onLastPage,
  showFirstLast,
  pageIndex,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  rowCount,
  totalCount,
  isLoading = false,
}: Readonly<DataTablePaginationProps>) {
  const rowsPerPageId = useId();
  // hasNextPage/hasPreviousPage are false on a real last page too, so gate on the callbacks.
  const isPaginated = onNextPage !== undefined || onPreviousPage !== undefined;

  if (!isPaginated && !showSelect) {
    return null;
  }

  // Each control gates on its own props, so DimeNestedTable, which passes none, renders none.
  const showRange = pageIndex !== undefined && pageSize !== undefined;
  const showPageSizeControl = pageSize !== undefined && onPageSizeChange !== undefined;
  const rangeText = showRange
    ? formatPaginationRangeText({ pageIndex, pageSize, rowCount, totalCount })
    : null;
  // Needs the range's props too, so a cursor-only table never shows a frozen "Page 1".
  const pageText = showRange
    ? formatPaginationPageText({ pageIndex, pageSize, rowCount, totalCount })
    : null;

  return (
    <div className="flex items-center justify-end gap-4 pt-3">
      {showSelect || (isPaginated && showPageSizeControl) ? (
        <div className="flex flex-1 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-6 lg:gap-8">
          {showSelect && (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel?.().rows.length ?? 0} selected
            </span>
          )}
          {isPaginated && showPageSizeControl && (
            <DimeField orientation="horizontal" className="w-fit">
              <DimeFieldLabel
                htmlFor={rowsPerPageId}
                className="whitespace-nowrap font-normal text-muted-foreground"
              >
                <span className="sm:hidden">Rows</span>
                <span className="hidden sm:inline">Rows per page</span>
              </DimeFieldLabel>
              <DimeSelect
                id={rowsPerPageId}
                aria-label="Rows per page"
                value={pageSize}
                disabled={isLoading}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {(pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS).map((size) => (
                  <DimeSelectOption key={size} value={size}>
                    {size}
                  </DimeSelectOption>
                ))}
              </DimeSelect>
            </DimeField>
          )}
        </div>
      ) : (
        <div className="flex-1" />
      )}
      {isPaginated && (
        <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-6 lg:gap-8">
          {rangeText !== null && (
            <div className="whitespace-nowrap text-sm text-muted-foreground">{rangeText}</div>
          )}
          <div className="flex items-center gap-4">
            {pageText !== null && (
              <div className="whitespace-nowrap text-sm text-muted-foreground">{pageText}</div>
            )}
            <DataTablePaginationNav
              isBackwardDisabled={isLoading || !hasPreviousPage}
              isForwardDisabled={isLoading || !hasNextPage}
              onFirstPage={onFirstPage}
              onLastPage={onLastPage}
              onNextPage={onNextPage}
              onPreviousPage={onPreviousPage}
              showFirstPageButton={(showFirstLast ?? true) && onFirstPage !== undefined}
              showLastPageButton={(showFirstLast ?? true) && onLastPage !== undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}
